import { InvoiceItemPayload } from "#src/api/InvoiceItem.ts";
import { PrivateSerieAttendancePayload } from "#src/api/PrivateSerieAttendance.ts";
import { StudentInfoRequestResponsePayload } from "#src/api/StudentInfoRequest.ts";
import {
  createPrivateSerie,
  createPrivateSerieVariables,
  filteredPrivateSeriesForUser,
  filteredPrivateSeriesForUserCounts,
  filteredPrivateSeriesForUserCountsVariables,
  filteredPrivateSeriesForUserVariables,
  privateSerie,
  privateSerieVariables,
  privateSeriesForInstructor,
  privateSeriesForInstructorVariables,
  privateSeriesForUser,
  privateSeriesForUserVariables,
  updatePrivateSerie,
  updatePrivateSerieVariables,
} from "#types/code-generator.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import { gql } from "graphql-tag";

const PrivateSeriePayloadFragment = gql`
  fragment PrivateSeriePayload on PrivateSerie {
    accountOwner {
      id
      address1
      credits {
        id
        amount
      }
      firstName
      lastName
      email
      phone
      stripeCustomerId
      stripeToken
      adyenToken
    }
    attendances {
      ...PrivateSerieAttendancePayload
    }
    availableDays
    createdAt
    createdBy {
      id
      firstName
      lastName
    }
    customerInfoRequestResponses {
      id
      answer
      question
      user {
        id
      }
    }
    events {
      id
      createdAt
      date
      type
      privateSerieAttendanceId
      by {
        id
        email
        firstName
        lastName
        level
      }
      to {
        id
        email
        firstName
        lastName
      }
      value
    }
    firstLessonEnd
    firstLessonStart
    id
    instructor {
      id
      email
      firstName
      lastName
    }
    instructorsToNotify {
      id
      email
      firstName
      lastName
    }
    invoiceItems {
      ...InvoiceItemPayload
      invoice {
        id
        createdAt
        invoiceDate
        invoiceItems {
          ...InvoiceItemPayload
        }
        status
      }
    }
    note
    numberOfLessons
    offering {
      id
      additionalFeeAmount
      addons {
        id
        title
        description
        billingCategory {
          id
          title
        }
        price
      }
      agreements {
        id
        title
        description
        required
      }
      allowAdditionalStudents
      allowExtension
      applyAnnualFee
      atAccountHome
      availableDays
      billingCategory {
        id
        title
      }
      charges {
        id
        amount
        description
        billingCategory {
          id
          title
        }
      }
      classTimes {
        id
        atAccountHome
        date
        day
        endTime
        location {
          id
          address1
          city
          state
          title
          zip
        }
        startTime
        zone {
          id
          title
        }
      }
      collectTaxes
      communications {
        id
        ruleAmount
        ruleAmountType
      }
      coupons {
        id
        amount
        availableForAllOfferings
        code
        expirationDate
        frequency
        title
        amountType
      }
      customerInfoRequests
      department {
        id
        active
        annualFeeAmount
        annualFeeMax
        annualFeeType
        contactName
        default
        email
        filters
        phone
        tags {
          id
          order
          title
        }
        title
      }
      description
      discounts {
        id
        amount
        max
        min
      }
      duration
      endDate
      excludedDates
      extensionDiscounts {
        id
        amount
        max
        min
      }
      extensionPrice
      extensionPriceAdjustments {
        id
        min
        price
      }
      extensionMaxAttendances
      extensionMinAttendances
      fees {
        id
        amount
        title
      }
      firstLessonStartDays
      firstLessonStart
      frequency
      hideDefaultStaff
      instructor {
        id
        email
        firstName
        lastName
      }
      location {
        id
        address1
        title
      }
      maxAge
      maxAgeType
      minAge
      minAgeType
      minAttendances
      multiClassDiscount
      multiStudentDiscount
      package
      participantType
      paymentFrequency
      price
      priceAdjustments {
        id
        min
        price
      }
      priceForAdditionalStudents
      program {
        id
        description
        title
      }
      publishDate
      publishOnWebsite
      registrationClose
      registrationOpen
      requestStartDate
      restrictReschedule
      restrictRescheduleType
      startDate
      status
      studentInfoRequests {
        id
        answers
        description
        noQuestion
        required
        slug
        type
        yesQuestion
      }
      tags {
        id
        title
      }
      title
      type
      unpublishDate
    }
    organization {
      id
      title
    }
    previousSerie {
      id
      createdAt
      events {
        id
        createdAt
        type
        by {
          id
          email
          firstName
          lastName
        }
        to {
          id
          email
          firstName
          lastName
        }
        value
      }
      invoiceItems {
        ...InvoiceItemPayload
      }
    }
    roster {
      id
      avatar {
        url
      }
      firstName
      lastName
      gender
      birthDate
    }
    scheduledThrough
    status
    studentInfoRequestResponses {
      ...StudentInfoRequestResponsePayload
    }
    updatedAt
  }
  ${StudentInfoRequestResponsePayload}
  ${PrivateSerieAttendancePayload}
  ${InvoiceItemPayload}
`;

export const UpdatePrivateSerieMutation = gql`
  mutation updatePrivateSerie(
    $data: PrivateSerieCreateInput!
    $where: UniqueInput!
  ) {
    updatePrivateSerie(data: $data, where: $where) {
      ...PrivateSeriePayload
    }
  }
  ${PrivateSeriePayloadFragment}
` as TypedDocumentNode<updatePrivateSerie, updatePrivateSerieVariables>;

export const CreatePrivateSerieMutation = gql`
  mutation createPrivateSerie($data: PrivateSerieCreateInput!) {
    createPrivateSerie(data: $data) {
      ...PrivateSeriePayload
    }
  }
  ${PrivateSeriePayloadFragment}
` as TypedDocumentNode<createPrivateSerie, createPrivateSerieVariables>;

export const PrivateSeriesForUserQuery = gql`
  query privateSeriesForUser(
    $where: PrivateSerieWhereInput!
    $skip: Int
    $take: Int
    $filter: String
  ) {
    privateSeries(where: $where, take: $take, skip: $skip, filter: $filter) {
      ...PrivateSeriePayload
    }
  }
  ${PrivateSeriePayloadFragment}
` as TypedDocumentNode<privateSeriesForUser, privateSeriesForUserVariables>;

export const FilteredPrivateSeriesForUserQuery = gql`
  query filteredPrivateSeriesForUser(
    $where: PrivateSerieWhereInput!
    $filter: String
    $skip: Int
    $take: Int
  ) {
    privateSeries(where: $where, filter: $filter, take: $take, skip: $skip) {
      ...PrivateSeriePayload
    }
  }
  ${PrivateSeriePayloadFragment}
` as TypedDocumentNode<
  filteredPrivateSeriesForUser,
  filteredPrivateSeriesForUserVariables
>;

export const PrivateSerieQuery = gql`
  query privateSerie($id: String!) {
    privateSerie(where: { id: $id }) {
      ...PrivateSeriePayload
    }
  }
  ${PrivateSeriePayloadFragment}
` as TypedDocumentNode<privateSerie, privateSerieVariables>;

export const FilteredPrivateSeriesCountsForUserQuery = gql`
  query filteredPrivateSeriesForUserCounts(
    $where: PrivateSerieWhereInput!
    $filter: String
  ) {
    privateSeriesCounts(where: $where, filter: $filter) {
      count
    }
  }
` as TypedDocumentNode<
  filteredPrivateSeriesForUserCounts,
  filteredPrivateSeriesForUserCountsVariables
>;

export const PrivateSeriesForInstructorQuery = gql`
  query privateSeriesForInstructor(
    $organizationId: String!
    $instructorId: String!
  ) {
    privateSeries(
      where: {
        organizationId: $organizationId
        instructorId: $instructorId
        status: { notIn: [Archived] }
      }
    ) {
      ...PrivateSeriePayload
    }
  }
  ${PrivateSeriePayloadFragment}
` as TypedDocumentNode<
  privateSeriesForInstructor,
  privateSeriesForInstructorVariables
>;
